import { ErrorBoundary } from '@sentry/react';
import Select from 'react-select';

import DateTimeSelector from 'components/TripScreen/TripSearchScreen/DateTimeSelector';
import ExitButton from 'components/ExitButton';
import CloseButton from 'components/CloseButton';
import Icon from 'components/Icon';
import LocationWaypoint from 'components/TripScreen/TripSearchScreen/LocationWaypoint';
import TripOptions from 'components/TripScreen/TripSearchScreen/TripOptions';
import LoadingSpinner from './LoadingSpinner';
import lokalise from 'utils/lokalise';
import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';

function TripSearchScreen({
    isPermanent,

    getTripPlan,

    startingLocation,
    endingLocation,
    setStartingLocation,
    setEndingLocation,
    startDate,
    startTime,
    setStartDate,
    setStartTime,
    searchingTrips,
    loading,
    setLoading,
    switchLocation,

    statusMessage,
    errorCount,

    searchAreaLocation,
    tripOptions,
    setTripOptions,
    selectTripOption,

    toggleTripScreen,
    onClickStatusMessage,

    shortCode,
    pgaLocations,
    disableInput,

    params
}) {

    const [makePlan, setMakePlan] = useState(false);
    const [leaveBy, setLeaveBy] = useState({
        label: 'Leaving at',
        value: 'leaveBy'
    });

    const [searchParams, setSearchParams] = useSearchParams();
    const leaveOptions = [{
        label: 'Leaving at',
        value: 'leaveBy'
    }, {
        label: 'Arriving by',
        value: 'arriveBy'
    }]
    const inputBackground = getComputedStyle(document.documentElement).getPropertyValue('--inputBackground').trim();
    const inputText = getComputedStyle(document.documentElement).getPropertyValue('--inputText').trim();



    function formatDateAndTime() {
        const offset = new Date().getTimezoneOffset() * 60000;
        const today = new Date(Date.now() - offset);
        const date = today.toISOString().substring(0, 10);
        const time = today.toISOString().substring(11, 16);
        return [date, time]
    }

    function submitTripPlan() {
        const defaultDateTime = formatDateAndTime();
        let submittedDate = startDate ? startDate : defaultDateTime[0];
        let submittedTime = startTime ? startTime : defaultDateTime[1];

        if (submittedDate) {
            submittedDate = submittedDate.replaceAll("-", "");
        }

        // console.log(submittedDate, submittedTime);
        getTripPlan(submittedDate, submittedTime)
    }

    function onTripSearch() {
        if (startingLocation && endingLocation) {
            setLoading(true);
            submitTripPlan();
        }
    }

    // Essentially just a back button depending on whether user searched or not
    function returnToSearch() {
        if (!tripOptions) {
            toggleTripScreen(false);
        }
        else {
            setTripOptions(null);
        }
    }

    function handleTripClose()  {
        toggleTripScreen(false);

        // Timeout for a more natural closing
        setTimeout(() => {
            setTripOptions(null);
        }, 2000);
    }

    return (
        <div className="TripSearchScreen">

            <div className="search-area">
                <header className="header">
                    {!isPermanent && <ExitButton showButton={true} tapEvent={returnToSearch} />}
                    {!isPermanent && <CloseButton showButton={true} tapEvent={handleTripClose} />}
                    {
                        tripOptions
                        ? <h1>{lokalise.getKey('ts13q')}</h1>
                        : <>
                            <LoadingSpinner loading={loading} />
                            <h1>{loading ? lokalise.getKey('ts13p') : lokalise.getKey('ts13n')}</h1>
                        </>
                    }
                </header>
                {
                    loading
                    ? <></>
                    : tripOptions //If there are trip options, don't show this
                    ? <></>
                    : <>
                        <div className="planner-options">
                            <div className="plan-option" onClick={() => {setMakePlan(false)}}>
                                <Icon category="ui" name="mapLocation" color={`${makePlan ? `#${params?.mainText || '000'}` : `#${params?.accentColor}`}`} />
                                <p style={{color: makePlan ? `#${params?.mainText || '000'}` : `#${params?.accentColor || '000'}`}}>Leave now</p>
                            </div>
                            <div className="gradient-line"></div>
                            <div className="plan-option" onClick={() => {setMakePlan(true)}}>
                                <Icon category="ui" name="calendar" color={`${makePlan ? `#${params?.accentColor}` : `#${params?.mainText || '000'}`}`} /> 
                                <p style={{color: makePlan ? `#${params?.accentColor || '000'} ` : `#${params?.mainText || '000'}`}}>Make a plan</p>
                            </div>
                        </div>
                        <div className="locations">
                            <Icon className="invert-vertical" category="ui" name="invertVertical" fillColor={`#${params?.accentColor ? params?.accentColor : '000'}`} onClick={switchLocation} />
                            <div className="waypoints">
                                <ErrorBoundary fallback={<>{lokalise.getKey("ts12d")}</>}>
                                    <h3>{lokalise.getKey('ts13j')}:</h3>
                                    <LocationWaypoint locked={searchParams.get('tpFrom') === 'locked'} placeholder={lokalise.getKey('ts10f')} location={startingLocation} searchAreaLocation={searchAreaLocation} updateLocation={setStartingLocation} shortCode={shortCode} pgaLocations={pgaLocations} isDisableInput={disableInput === 'top'} />
                                    <h3>{lokalise.getKey('ts13k')}:</h3>
                                    <LocationWaypoint locked={searchParams.get('tpTo') === 'locked'} placeholder={lokalise.getKey('ts10g')} location={endingLocation} searchAreaLocation={searchAreaLocation} updateLocation={setEndingLocation} shortCode={shortCode} pgaLocations={pgaLocations} isDisableInput={disableInput === 'bottom'} />
                                </ErrorBoundary>
                            </div>
                        </div>
                        <div className="advanced" style={{ display: makePlan ? '' : 'none' }}>
                            <DateTimeSelector locked={searchParams.get('tpDate') === 'locked'} type='date' startDate={startDate} setStartDate={setStartDate} startTime={startTime} setStartTime={setStartTime} />
                            <div className="datetime-container">
                                <Select className="dropdown" options={leaveOptions} value={leaveBy} onChange={(e) => setLeaveBy({value: e.value, label: e.label})} styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        backgroundColor: inputBackground,
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        color: inputText || `#${params?.mainText || '000'}`,
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        backgroundColor: '#000', // <-- dropdown menu background
                                        color: '#fff',           // optional: dropdown text color
                                    }),
                                    option: (provided, state) => ({
                                        ...provided,
                                        color: '#fff',           // dropdown option text color
                                        backgroundColor: state.isFocused ? '#222' : '#000', // highlight on hover
                                    }),
                                }} />
                                <DateTimeSelector locked={searchParams.get('tpTime') === 'locked'} type='time' startDate={startDate} setStartDate={setStartDate} startTime={startTime} setStartTime={setStartTime} />
                            </div>
                        </div>
                        <button className="search-button" onClick={() => onTripSearch()}>{lokalise.getKey('ts13o')}</button>
                    </>
                }
            </div>

            <StatusMessage statusMessage={statusMessage} errorCount={errorCount} onClickStatusMessage={onClickStatusMessage} params={params} />

            {/* !tripOptions && <MobilityScoreBadge startLocation={startingLocation} /> */}

            <TripOptions tripOptions={tripOptions} selectTripOption={selectTripOption} searchingTrips={searchingTrips} params={params} />

            {/* <PoweredByAF /> */}
        </div>
    )
}

function StatusMessage({ statusMessage, errorCount, onClickStatusMessage, params }) {
    return (
        <div className={`status-message ${statusMessage ? 'visible' : ''}`} onClick={onClickStatusMessage}>
            <div className="text">{statusMessage}</div>
        </div>
    )
}

export default TripSearchScreen;
